import { Box } from "@mui/material";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef, useState } from "react";

import { EditorBubbleMenu } from "Shared/TextEditor/EditorBubbleMenu";
import { EditorFloatingMenu } from "Shared/TextEditor/EditorFloatingMenu";
import { EditorToolbar } from "Shared/TextEditor/EditorToolbar";
import { EditorWrapper } from "Shared/TextEditor/editorStyles";

export interface TipTapEditorProps {
  value: string;
  onChange: (html: string, plainText: string) => void;
  placeholder?: string;
  hasError?: boolean;
}

export const TipTapEditor = ({
  value,
  onChange,
  placeholder,
  hasError,
}: TipTapEditorProps): React.ReactNode => {
  const isInternalUpdate = useRef(false);
  const [, setSelectionTick] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Superscript,
      Subscript,
      Highlight,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: placeholder ?? "",
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      const html = editor.getHTML();
      const plainText = editor.getText();
      onChange(html, plainText);
    },
    onTransaction: () => {
      setSelectionTick((n) => n + 1);
    },
  });

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box>
      <EditorToolbar editor={editor} />
      <EditorWrapper $hasError={hasError}>
        <EditorContent editor={editor} />
      </EditorWrapper>
      <EditorBubbleMenu editor={editor} />
      <EditorFloatingMenu editor={editor} />
    </Box>
  );
};
